import { useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import Worker from '@/utils/background-tasks';
import { traceBoot } from '@tcap/data-router/utils/bootTrace';
import { DashboardMenuProvider } from '@/context/DashboardMenuContext';
import { LayoutProvider } from '@/context/LayoutContext';
import Sidebar from '@/components/sidebar/Sidebar';
import Navbar from '@/pages/navbar/Navbar';
import WholesalerPaymentBanner from '@/components/banners/WholesalerPaymentBanner';
import {
    shouldShowWholesalerPaymentBanner,
    getContentTopOffsetCss,
} from '@/lib/data-router/hooks/useThemeHelper';

import '@/App.css';
import '@/console.js';

import {
    DRAWER_WIDTH,
    DRAWER_COLLAPSED_WIDTH,
    CONTENT_MIN_WIDTH,
    DRAWER_Z_INDEX,
    NAVBAR_Z_INDEX,
    layoutStylesFn,
} from './constants';

function ProtectedLayout() {
    const hasLoggedRenderRef = useRef(false);
    const dispatch = useDispatch();
    const isMenuCollapse = useSelector((state) => state.menu.collapse);
    const drawerWidth = isMenuCollapse ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;
    const layoutStyles = layoutStylesFn(drawerWidth);

    const {
        token,
        reLogin,
        showMFA,
        defaultPartner,
        defaultCompany,
        defaultWholesaler,
        requiredScope,
        wholesalerPaymentBanner,
    } = useSelector((state) => state.login);
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const { darkMode } = useSelector((state) => state.settings);
    const contentTopOffset = getContentTopOffsetCss(
        shouldShowWholesalerPaymentBanner(
            requiredScope,
            wholesalerPaymentBanner,
        ),
    );

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    if (!hasLoggedRenderRef.current) {
        hasLoggedRenderRef.current = true;
        traceBoot('protected-layout.render', {
            pathname: location.pathname,
        });
    }

    const wholesalerID = queryParams.get('wholesalerID');
    const partnerID = queryParams.get('partnerID');
    const companyID = queryParams.get('companyID');

    useEffect(() => {
        if (
            (defaultPartner || defaultCompany || defaultWholesaler) &&
            !currentWholesaler
        ) {
            window.history.replaceState(null, '', '/ui' + location.pathname);
            //if current stuffs arent set yet, set them to default.
            dispatch({
                type: 'CHANGE_CURRENT_PARTNER',
                payload: partnerID || defaultPartner,
            });
            dispatch({
                type: 'CHANGE_CURRENT_COMPANY',
                payload: companyID || defaultCompany,
            });
            dispatch({
                type: 'CHANGE_CURRENT_WHOLESALER',
                payload: wholesalerID || defaultWholesaler,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWholesaler]);

    useLayoutEffect(() => {
        traceBoot('protected-layout.commit', {
            pathname: location.pathname,
        });
    }, [location.pathname]);

    useEffect(() => {
        traceBoot('protected-layout.mount', {
            pathname: location.pathname,
        });
    }, [location.pathname]);

    // NOTE: Hot fix for #2187 and #2554 AG Grid popups not closing when clicking outside of them.
    useEffect(() => {
        const handleClickOutsidePopup = (event) => {
            const popups = document.querySelectorAll('.ag-popup');
            const clickedInsideAny = Array.from(popups).some((popup) =>
                popup.contains(event.target),
            );
            if (!clickedInsideAny) {
                popups.forEach((popup) => popup.remove());
            }
        };

        // Capture phase so this runs before AG Grid's own mousedown handlers
        // (e.g. the pagination page-size picker) open a new popup — otherwise
        // the just-opened popup gets treated as a stale one and removed
        // immediately, since it's appended under document.body rather than
        // nested inside the element that was clicked.
        document.addEventListener('mousedown', handleClickOutsidePopup, true);
        return () =>
            document.removeEventListener(
                'mousedown',
                handleClickOutsidePopup,
                true,
            );
    }, []);

    if (!token || reLogin || showMFA) {
        traceBoot('protected-layout.navigate-login', {
            pathname: location.pathname,
        });
        return <Navigate to="/" replace />;
    }

    return (
        <Box
            display="flex"
            bg={darkMode ? '#121212' : 'white'}
            sx={layoutStyles}>
            <LayoutProvider>
                <Box
                    as="header"
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    zIndex={NAVBAR_Z_INDEX}
                    bg="dark.bgDark">
                    <Worker />
                    <WholesalerPaymentBanner />
                    <Navbar />
                </Box>
                <Box
                    as="nav"
                    width={`${drawerWidth}px`}
                    flexShrink={0}
                    transition="width 0.4s ease-in-out"
                    bg="dark.bgDark">
                    <Box
                        position="fixed"
                        top={0}
                        left={0}
                        sx={{
                            height: '100vh',
                            '@supports (height: 100dvh)': {
                                height: '100dvh',
                            },
                        }}
                        width={`${drawerWidth}px`}
                        zIndex={DRAWER_Z_INDEX}
                        bg="dark.bgDark"
                        boxShadow="0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)"
                        overflowX="hidden">
                        <Sidebar />
                    </Box>
                </Box>
                <Box
                    as="main"
                    flexGrow={1}
                    minW={`${CONTENT_MIN_WIDTH}px`}
                    sx={{
                        [`@media (max-width: ${
                            CONTENT_MIN_WIDTH + drawerWidth
                        }px)`]: {
                            minWidth: 0,
                        },
                    }}
                    p={6}
                    pt={0}
                    mt={contentTopOffset}>
                    <DashboardMenuProvider>
                        <Outlet />
                    </DashboardMenuProvider>
                </Box>
            </LayoutProvider>
        </Box>
    );
}

export default ProtectedLayout;
